import React, { useRef, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  CardHeader,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Divider,
  Tooltip,
  Chip,
  Tabs,
  Tab,
  Badge,
  Paper,
  Alert,
  FormHelperText,
} from '@mui/material';
import { RouterLink as Link } from '../common/RouterLink';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowForwardIos from '@mui/icons-material/ArrowForwardIos';
import PersonIcon from '@mui/icons-material/Person';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useCases, Patient } from '../../contexts/api-contexts/CasesContext';
import { useAnimalTypes } from '../../contexts/api-contexts/AnimalTypeContext';
import { useBreeds } from '../../contexts/api-contexts/BreedsContext';
import { gekkoApi } from '../../api/GekkoApi';
import AttachmentViewDialog from './AttachmentViewDialog';
import AttachmentUpload, { UploadedAttachment } from './AttachmentUpload';

interface FileInfo {
  id: string;
  name: string;
  size: number;
  type: string;
  extractedText?: string;
}

interface CaseBasicInfoProps {
  id?: string;
  onNavigate?: (path: string) => void;
  onTabChange?: (
    tab: 'basic' | 'symptoms' | 'diagnoses' | 'treatments'
  ) => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`case-tabpanel-${index}`}
      aria-labelledby={`case-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `case-tab-${index}`,
    'aria-controls': `case-tabpanel-${index}`,
  };
}

export const CaseBasicInfo: React.FC<CaseBasicInfoProps> = ({
  id,
  onNavigate,
  onTabChange,
}) => {
  const {
    currentCase,
    setCaseProperty,
    isSaving,
    persistCase,
    refreshCase,
    fetchCase,
    validationErrors,
  } = useCases();

  const [localNotes, setLocalNotes] = useState<string>('');

  const [isCreating, setIsCreating] = useState(false);
  const [files, setFiles] = useState<FileInfo[]>([]);
  const [selectedFile, setSelectedFile] = useState<FileInfo | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // New state for attachment dialog
  const [attachmentDialogOpen, setAttachmentDialogOpen] = useState(false);
  const [selectedAttachmentId, setSelectedAttachmentId] = useState<string>('');

  // New state for attachment uploads
  const [uploadedAttachments, setUploadedAttachments] = useState<
    UploadedAttachment[]
  >([]);

  // Tab state
  const [tabValue, setTabValue] = useState(0);

  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Determines if the case is new or existing
  const isNewCase = (!currentCase || !currentCase.id) && !id;

  // Get case attachments (from new structure or fallback to old)
  const caseAttachments = currentCase?.attachments || [];

  // Update files when current case changes
  useEffect(() => {
    if (currentCase && currentCase.files) {
      setFiles(currentCase.files);
    }
  }, [currentCase]);

  // Clear uploaded attachments when case attachments are updated from server
  // (uploaded attachments are only for locally uploaded files not yet saved)
  useEffect(() => {
    if (currentCase?.attachments) {
      // Filter out any uploaded attachments that are now in the case attachments
      const caseAttachmentIds = new Set(
        currentCase.attachments.map((att) => att.id)
      );
      setUploadedAttachments((prev) =>
        prev.filter((att) => !caseAttachmentIds.has(att.id))
      );
    }
  }, [currentCase?.attachments]);

  // Handle typing detection and case creation for new cases
  useEffect(() => {
    if (isNewCase && localNotes.trim() !== '' && !isCreating) {
      // Clear previous timeout if exists
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }

      // Set timeout for 5 seconds after typing stops
      typingTimeoutRef.current = setTimeout(() => {
        createNewCase(localNotes);
      }, 500); // 5 seconds of inactivity
    }

    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, [localNotes, isNewCase, isCreating]); // eslint-disable-line react-hooks/exhaustive-deps

  // Create a new case
  const createNewCase = async (initialNotes: string) => {
    if (isCreating) return;

    try {
      setIsCreating(true);

      // Get attachment IDs from successfully uploaded attachments
      const attachmentIds = uploadedAttachments
        .filter((att) => att.status === 'uploaded' && att.id)
        .map((att) => att.id);

      const response = await gekkoApi.post<{
        id?: string;
        notes: string;
        files?: FileInfo[];
        attachments?: Array<{ id: string; status: string }>;
      }>('/cases', {
        body: {
          notes: initialNotes,
          attachments:
            attachmentIds.length > 0
              ? attachmentIds.map((id) => ({ id }))
              : undefined,
        },
      });

      // Update the URL with the new case ID without reloading the page
      onNavigate?.(`/cases/${response.id}?tab=basic`);

      // Fetch the newly created case to populate the context
      if (response.id) {
        await fetchCase(response.id);
      }
    } catch (error) {
      console.error('Error creating case:', error);
    } finally {
      setIsCreating(false);
    }
  };

  // Handle file deletion
  const handleFileDelete = async (fileId: string) => {
    if (!id) return;

    try {
      await gekkoApi.delete(`/cases/${id}/files/${fileId}`);
      const updatedFiles = files.filter((file) => file.id !== fileId);
      setFiles(updatedFiles);

      // Update the current case with the new files list
      if (currentCase) {
        setCaseProperty('files', updatedFiles);
      }

      setIsDialogOpen(false);
      setSelectedFile(null);
    } catch (error) {
      console.error('Error deleting file:', error);
    }
  };

  // Open file dialog
  const openFileDialog = (file: FileInfo) => {
    setSelectedFile(file);
    setIsDialogOpen(true);
  };

  // Open attachment dialog
  const openAttachmentDialog = (attachmentId: string) => {
    setSelectedAttachmentId(attachmentId);
    setAttachmentDialogOpen(true);
  };

  // Close attachment dialog
  const closeAttachmentDialog = () => {
    setAttachmentDialogOpen(false);
    setSelectedAttachmentId('');
  };

  // Get status color for attachment chip
  const getAttachmentStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'processed':
        return 'success';
      case 'processing':
        return 'warning';
      case 'error':
        return 'error';
      case 'uploaded':
        return 'info';
      default:
        return 'default';
    }
  };

  // Handle attachment changes from AttachmentUpload component
  const handleAttachmentsChange = async (attachments: UploadedAttachment[]) => {
    setUploadedAttachments(attachments);

    // If we have a current case, update the attachments property and persist
    if (currentCase?.id) {
      const caseAttachments = attachments
        .filter((att) => att.status === 'uploaded' && att.id)
        .map((att) => ({ id: att.id, status: att.status.toUpperCase() }));
      setCaseProperty('attachments', caseAttachments);

      // Auto-save attachments to the case
      try {
        await persistCase();
      } catch (error) {
        console.error('Error saving attachments to case:', error);
      }
    }
  };

  // Handle tab change
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Handle file drop on textarea
  const handleTextAreaDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();

    const files = Array.from(event.dataTransfer.files);
    if (files.length > 0) {
      // Switch to attachments tab to show upload progress
      setTabValue(1);

      // Trigger the attachment upload process
      const fileInput = document.createElement('input');
      fileInput.type = 'file';
      fileInput.multiple = true;
      fileInput.style.display = 'none';

      // Create a FileList-like object
      const dataTransfer = new DataTransfer();
      files.forEach((file) => dataTransfer.items.add(file));
      fileInput.files = dataTransfer.files;

      // Trigger change event to upload files
      const changeEvent = new Event('change', { bubbles: true });
      fileInput.dispatchEvent(changeEvent);

      // Create new attachments for immediate UI feedback
      const newAttachments: UploadedAttachment[] = files.map((file) => ({
        id: '', // Will be set after getting upload URL
        filename: file.name,
        size: file.size,
        status: 'uploading' as const,
      }));

      // Add to current attachments
      const updatedAttachments = [...uploadedAttachments, ...newAttachments];
      setUploadedAttachments(updatedAttachments);

      // Process file uploads manually
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const attachmentIndex = uploadedAttachments.length + i;

        try {
          // Get presigned upload URL
          const response = await gekkoApi.post<{
            id: string;
            upload_url: string;
          }>('/cases/attachments', {
            body: {
              filename: file.name,
              mime_type: file.type,
            },
          });

          // Update attachment with ID
          const currentAttachments = [...updatedAttachments];
          currentAttachments[attachmentIndex] = {
            ...currentAttachments[attachmentIndex],
            id: response.id,
          };
          setUploadedAttachments(currentAttachments);

          // Upload file to S3
          const uploadResponse = await fetch(response.upload_url, {
            method: 'PUT',
            body: file,
            headers: {
              'Content-Type': file.type,
            },
          });

          if (!uploadResponse.ok) {
            throw new Error(`Upload failed: ${uploadResponse.statusText}`);
          }

          // Mark as uploaded
          const finalAttachments = [...currentAttachments];
          finalAttachments[attachmentIndex] = {
            ...finalAttachments[attachmentIndex],
            status: 'uploaded',
          };
          setUploadedAttachments(finalAttachments);
        } catch (error) {
          console.error('Error uploading file:', error);

          // Mark as error
          const errorAttachments = [...updatedAttachments];
          errorAttachments[attachmentIndex] = {
            ...errorAttachments[attachmentIndex],
            status: 'error',
            error: error instanceof Error ? error.message : 'Upload failed',
          };
          setUploadedAttachments(errorAttachments);
        }
      }
    }
  };

  // Handle drag over for textarea
  const handleTextAreaDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  // Get total attachment count for badge
  const getTotalAttachmentCount = () => {
    return (
      caseAttachments.length +
      files.length +
      uploadedAttachments.filter((att) => att.status === 'uploaded').length
    );
  };

  // Handle process text (save and retrigger processing)
  const handleProcessText = async () => {
    try {
      await persistCase();
      // Trigger processing by refetching the case
      await refreshCase();
    } catch (error) {
      console.error('Error processing text:', error);
    }
  };

  // Add patient information state management
  const { animalTypes } = useAnimalTypes();
  const { breeds, fetchBreedsByAnimalTypeId } = useBreeds();

  // Patient information state
  const [patientInfo, setPatientInfo] = useState<Patient | null>(
    currentCase?.patient || null
  );

  // Helper to get field-specific validation error
  const getFieldError = (fieldName: string): string | undefined => {
    if (!validationErrors) return undefined;
    // Check for patient.fieldName format (e.g., "patient.weight_kg")
    return validationErrors[`patient.${fieldName}`];
  };

  // Get general error (non-field-specific)
  const generalError = validationErrors?.['_'];

  // Handle patient info change
  const handlePatientInfoChange = (field: keyof Patient, value: any) => {
    // Create a new patient object or update the existing one
    const updatedPatient = patientInfo
      ? { ...patientInfo, [field]: value }
      : {
          animal_type_id: 0,
          breed_id: 0,
          weight_kg: 0,
          [field]: value,
        };

    setPatientInfo(updatedPatient);

    // Update current case if it exists
    if (currentCase) {
      setCaseProperty('patient', updatedPatient);
    }
  };

  // Fetch breeds when animal type changes
  useEffect(() => {
    if (patientInfo?.animal_type_id) {
      fetchBreedsByAnimalTypeId(patientInfo.animal_type_id);
    }
  }, [patientInfo?.animal_type_id, fetchBreedsByAnimalTypeId]);

  // Update patient info when case changes
  useEffect(() => {
    if (currentCase?.patient) {
      setPatientInfo(currentCase.patient);
    }
  }, [currentCase?.patient]);

  return (
    <>
      {/* Case Notes Section - Always Visible */}
      <Card sx={{ mb: 3 }}>
        <CardHeader title="Case Notes" />
        <CardContent>
          <TextField
            fullWidth
            multiline
            minRows={12}
            placeholder="Enter your case notes here or drop files to attach them..."
            value={currentCase ? currentCase.notes : localNotes}
            onChange={(e) =>
              currentCase
                ? setCaseProperty('notes', e.target.value)
                : setLocalNotes(e.target.value)
            }
            variant="outlined"
            onDrop={handleTextAreaDrop}
            onDragOver={handleTextAreaDragOver}
            sx={{
              '& .MuiInputBase-root': {
                transition: 'border-color 0.2s ease',
              },
              '& .MuiInputBase-root:hover': {
                borderColor: 'primary.main',
              },
            }}
          />
          {/* Bottom row with Process text button on left and Continue link on right */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mt: 2,
            }}
          >
            <Button
              variant="outlined"
              size="small"
              onClick={handleProcessText}
              disabled={!currentCase?.id || isSaving || isCreating}
            >
              {isSaving ? 'Processing...' : 'Process text'}
            </Button>

            <Tooltip
              title={
                !currentCase?.patient
                  ? 'Fill in patient information to continue'
                  : isCreating
                  ? 'Wait for processing to complete'
                  : ''
              }
            >
              <span>
                <Link
                  to={`/cases/${currentCase?.id}?tab=symptoms`}
                  style={{
                    textDecoration: 'none',
                    pointerEvents:
                      !currentCase?.patient || isCreating ? 'none' : 'auto',
                    opacity: !currentCase?.patient || isCreating ? 0.5 : 1,
                  }}
                  onClick={(e) => {
                    if (!currentCase?.patient || isCreating) {
                      e.preventDefault();
                      return;
                    }
                    e.preventDefault();
                    onTabChange?.('symptoms');
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      color: 'primary.main',
                      fontSize: '1.1rem',
                      fontWeight: 'medium',
                      '&:hover': {
                        color: 'primary.dark',
                      },
                    }}
                  >
                    Continue to Symptoms
                    <ArrowForwardIos sx={{ fontSize: '1rem' }} />
                  </Box>
                </Link>
              </span>
            </Tooltip>
          </Box>
        </CardContent>
      </Card>

      {/* Tabs for Patient Information and Attachments */}
      <Paper sx={{ width: '100%' }}>
        {/* Tabs Header */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="case tabs"
          >
            <Tab
              icon={<PersonIcon />}
              label="Patient Information"
              {...a11yProps(0)}
              sx={{ textTransform: 'none' }}
            />
            <Tab
              icon={
                <Badge badgeContent={getTotalAttachmentCount()} color="primary">
                  <AttachFileIcon />
                </Badge>
              }
              label="Attachments"
              {...a11yProps(1)}
              sx={{ textTransform: 'none' }}
            />
          </Tabs>
        </Box>

        {/* Tab Content */}
        <Box sx={{ minHeight: '40vh' }}>
          {/* Tab 1: Patient Information */}
          <TabPanel value={tabValue} index={0}>
            <Grid container spacing={3}>
              {/* Patient Information Section */}
              <Grid item xs={12}>
                <Card>
                  <CardHeader title="Patient Information" />
                  <CardContent>
                    {generalError && (
                      <Alert severity="error" sx={{ mb: 2 }}>
                        {generalError}
                      </Alert>
                    )}
                    {!currentCase?.patient && !patientInfo && (
                      <>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 2 }}
                        >
                          Patient information will be extracted from your notes
                          if possible. You can also manually enter the
                          information below.
                        </Typography>
                        <Divider sx={{ my: 2 }} />
                      </>
                    )}

                    <Box>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Patient Name"
                            variant="outlined"
                            value={patientInfo?.name || ''}
                            onChange={(e) =>
                              handlePatientInfoChange('name', e.target.value)
                            }
                            error={!!getFieldError('name')}
                            helperText={getFieldError('name')}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <FormControl
                            fullWidth
                            error={!!getFieldError('animal_type_id')}
                          >
                            <InputLabel id="animal-type-label">
                              Animal Type
                            </InputLabel>
                            <Select
                              labelId="animal-type-label"
                              id="animal-type-select"
                              value={patientInfo?.animal_type_id || ''}
                              label="Animal Type"
                              onChange={(e) =>
                                handlePatientInfoChange(
                                  'animal_type_id',
                                  e.target.value
                                )
                              }
                            >
                              {animalTypes.map((type) => (
                                <MenuItem key={type.id} value={type.id}>
                                  {type.name}
                                </MenuItem>
                              ))}
                            </Select>
                            {getFieldError('animal_type_id') && (
                              <FormHelperText>
                                {getFieldError('animal_type_id')}
                              </FormHelperText>
                            )}
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            type="number"
                            label="Weight (kg)"
                            variant="outlined"
                            value={patientInfo?.weight_kg || ''}
                            onChange={(e) =>
                              handlePatientInfoChange(
                                'weight_kg',
                                parseFloat(e.target.value)
                              )
                            }
                            error={!!getFieldError('weight_kg')}
                            helperText={getFieldError('weight_kg')}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <FormControl
                            fullWidth
                            error={!!getFieldError('breed_id')}
                          >
                            <InputLabel id="breed-label">Breed</InputLabel>
                            <Select
                              labelId="breed-label"
                              id="breed-select"
                              value={patientInfo?.breed_id || ''}
                              label="Breed"
                              disabled={!patientInfo?.animal_type_id}
                              onChange={(e) =>
                                handlePatientInfoChange(
                                  'breed_id',
                                  e.target.value
                                )
                              }
                            >
                              {patientInfo?.animal_type_id &&
                                breeds[patientInfo.animal_type_id]?.map(
                                  (breed) => (
                                    <MenuItem key={breed.id} value={breed.id}>
                                      {breed.name}
                                    </MenuItem>
                                  )
                                )}
                            </Select>
                            {getFieldError('breed_id') && (
                              <FormHelperText>
                                {getFieldError('breed_id')}
                              </FormHelperText>
                            )}
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            type="date"
                            label="Date of Birth"
                            InputLabelProps={{ shrink: true }}
                            variant="outlined"
                            value={patientInfo?.date_of_birth || ''}
                            onChange={(e) =>
                              handlePatientInfoChange(
                                'date_of_birth',
                                e.target.value
                              )
                            }
                            error={!!getFieldError('date_of_birth')}
                            helperText={getFieldError('date_of_birth')}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <FormControl
                            fullWidth
                            error={!!getFieldError('gender_id')}
                          >
                            <InputLabel id="gender-label">Gender</InputLabel>
                            <Select
                              labelId="gender-label"
                              id="gender-select"
                              value={patientInfo?.gender_id || ''}
                              label="Gender"
                              onChange={(e) =>
                                handlePatientInfoChange(
                                  'gender_id',
                                  e.target.value
                                )
                              }
                            >
                              <MenuItem value={1}>Male</MenuItem>
                              <MenuItem value={2}>Female</MenuItem>
                            </Select>
                            {getFieldError('gender_id') && (
                              <FormHelperText>
                                {getFieldError('gender_id')}
                              </FormHelperText>
                            )}
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <FormControl error={!!getFieldError('neutered')}>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={patientInfo?.neutered || false}
                                  onChange={(e) =>
                                    handlePatientInfoChange(
                                      'neutered',
                                      e.target.checked
                                    )
                                  }
                                />
                              }
                              label="Neutered"
                            />
                            {getFieldError('neutered') && (
                              <FormHelperText>
                                {getFieldError('neutered')}
                              </FormHelperText>
                            )}
                          </FormControl>
                        </Grid>
                      </Grid>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>

          {/* Tab 2: Attachments */}
          <TabPanel value={tabValue} index={1}>
            <Grid container spacing={3}>
              {/* Attachment Upload Section */}
              <Grid item xs={12}>
                <Card>
                  <CardHeader
                    title="Attachments"
                    subheader={`${getTotalAttachmentCount()} attachment${
                      getTotalAttachmentCount() !== 1 ? 's' : ''
                    }`}
                  />
                  <CardContent>
                    {/* Attachment Upload Component */}
                    <AttachmentUpload
                      attachments={uploadedAttachments}
                      onAttachmentsChange={handleAttachmentsChange}
                      disabled={false}
                      maxFiles={10}
                      acceptedTypes={[
                        '.pdf',
                        '.jpg',
                        '.jpeg',
                        '.png',
                        '.txt',
                        '.doc',
                        '.docx',
                      ]}
                    />

                    <Box
                      sx={{
                        p: 2,
                        border: '1px solid #e0e0e0',
                        borderRadius: 1,
                        minHeight: '200px',
                        mt: 2,
                      }}
                    >
                      {/* Show case attachments if available */}
                      {caseAttachments.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Case Attachments ({caseAttachments.length})
                          </Typography>
                          <List dense>
                            {caseAttachments.map((attachment) => (
                              <ListItem
                                key={attachment.id}
                                button
                                onClick={() =>
                                  openAttachmentDialog(attachment.id)
                                }
                                sx={{
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  borderRadius: 1,
                                  mb: 1,
                                }}
                              >
                                <ListItemText
                                  primary={`Attachment ${attachment.id}`}
                                  secondary={
                                    <Chip
                                      label={attachment.status}
                                      size="small"
                                      color={
                                        getAttachmentStatusColor(
                                          attachment.status
                                        ) as any
                                      }
                                      sx={{ mt: 0.5 }}
                                    />
                                  }
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Box>
                      )}

                      {/* Show uploaded files if available */}
                      {files.length > 0 && (
                        <Box>
                          <Typography variant="subtitle2" gutterBottom>
                            Uploaded Files ({files.length})
                          </Typography>
                          <List dense>
                            {files.map((file) => (
                              <ListItem
                                key={file.id}
                                button
                                onClick={() => openFileDialog(file)}
                                sx={{
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  borderRadius: 1,
                                  mb: 1,
                                }}
                              >
                                <ListItemText
                                  primary={file.name}
                                  secondary={`${(file.size / 1024).toFixed(
                                    2
                                  )} KB`}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Box>
                      )}

                      {/* Show message when no attachments */}
                      {caseAttachments.length === 0 &&
                        files.length === 0 &&
                        uploadedAttachments.length === 0 && (
                          <Box sx={{ p: 2, textAlign: 'center' }}>
                            <CloudUploadIcon
                              sx={{
                                fontSize: 48,
                                color: 'grey.400',
                                mb: 1,
                              }}
                            />
                            <Typography variant="body2" color="text.secondary">
                              No attachments found. Use the upload area above to
                              add files.
                            </Typography>
                          </Box>
                        )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>
        </Box>
      </Paper>

      {/* File details dialog */}
      <Dialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        {selectedFile && (
          <>
            <DialogTitle>
              {selectedFile.name}
              <IconButton
                aria-label="delete"
                onClick={() => handleFileDelete(selectedFile.id)}
                sx={{ position: 'absolute', right: 8, top: 8 }}
              >
                <DeleteIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              {selectedFile.extractedText ? (
                <TextField
                  fullWidth
                  multiline
                  minRows={15}
                  value={selectedFile.extractedText}
                  variant="outlined"
                  InputProps={{ readOnly: true }}
                />
              ) : (
                <Typography variant="body1">
                  No text content could be extracted from this file.
                </Typography>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setIsDialogOpen(false)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Attachment View Dialog */}
      <AttachmentViewDialog
        open={attachmentDialogOpen}
        onClose={closeAttachmentDialog}
        attachmentId={selectedAttachmentId}
      />
    </>
  );
};

export default CaseBasicInfo;
